import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FiX, FiPackage, FiCheck, FiAlertTriangle } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BatchDeliveryModal({ hold, getAuthHeader, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [meta, setMeta] = useState(null);
  const [fee, setFee] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchCandidates = useCallback(async () => {
    if (!hold) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API}/admin/users/${hold.user_id}/delivery-batch-candidates/${hold.id}`,
        { headers: getAuthHeader() }
      );
      setCandidates(res.data.candidates || []);
      setMeta({
        user: res.data.user,
        requested_pickup_date: res.data.requested_pickup_date,
      });
      // Pre-select base hold + every sibling on same pickup date
      const pre = {};
      (res.data.candidates || []).forEach((c) => {
        pre[c.id] = true;
      });
      setSelected(pre);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load batch candidates');
    } finally {
      setLoading(false);
    }
  }, [hold, getAuthHeader]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const toggle = (id) => {
    if (id === hold.id) return; // base hold always selected
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const feeNum = parseFloat(fee || '0') || 0;
  const currentBalance = meta?.user?.delivery_balance ?? 0;
  const projectedBalance = currentBalance - feeNum;

  const handleSubmit = async () => {
    if (feeNum < 0) {
      setError('Delivery fee cannot be negative');
      return;
    }
    if (selectedIds.length === 0) {
      setError('Select at least one hold');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/admin/deliveries/complete`,
        {
          hold_ids: selectedIds,
          delivery_charge: feeNum,
          notes: notes.trim() || null,
        },
        { headers: getAuthHeader() }
      );
      onSuccess?.(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to mark delivered');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="batch-delivery-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <FiPackage className="text-purple-600" size={22} />
            <h2 className="text-lg font-bold">Mark Delivered</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700"
            data-testid="batch-delivery-close"
          >
            <FiX size={22} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading delivery batch…</div>
        ) : (
          <div className="p-6 space-y-5">
            {meta?.user && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
                <div className="text-sm text-gray-600">Delivering to</div>
                <div className="font-semibold text-gray-900">
                  {meta.user.name}{' '}
                  <span className="text-gray-500 font-normal text-sm">({meta.user.email})</span>
                </div>
                <div className="text-sm mt-1">
                  Pickup date:{' '}
                  <span className="font-medium">
                    {meta.requested_pickup_date
                      ? new Date(meta.requested_pickup_date).toLocaleDateString()
                      : '—'}
                  </span>
                </div>
                <div className="text-sm mt-1">
                  Current balance:{' '}
                  <span
                    className={`font-semibold ${
                      currentBalance < 0 ? 'text-red-600' : 'text-green-700'
                    }`}
                    data-testid="current-delivery-balance"
                  >
                    ₹{currentBalance.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div>
              <div className="text-sm font-semibold text-gray-800 mb-2">
                Holds being delivered today
              </div>
              <div className="space-y-2 border rounded-lg divide-y">
                {candidates.map((c) => {
                  const isBase = c.id === hold.id;
                  const isChecked = !!selected[c.id];
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                        isBase ? 'bg-gray-50' : ''
                      }`}
                      data-testid={`batch-candidate-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isBase}
                        onChange={() => toggle(c.id)}
                        className="w-4 h-4"
                        data-testid={`batch-candidate-check-${c.id}`}
                      />
                      {c.book?.cover_image_url && (
                        <img
                          src={c.book.cover_image_url}
                          alt=""
                          className="w-8 h-10 object-cover rounded"
                        />
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {c.book?.title || c.book_id}
                          {isBase && (
                            <span className="ml-2 text-xs text-purple-600">(this hold)</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {c.book?.authors?.join?.(', ') || ''}
                          {' • '}
                          {c.status}
                        </div>
                      </div>
                    </label>
                  );
                })}
                {candidates.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-500">No holds found.</div>
                )}
              </div>
              {candidates.length > 1 && (
                <p className="text-xs text-gray-500 mt-2">
                  Tip: select all the books you&apos;ll hand over on this trip so the delivery fee is
                  charged only once.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                Delivery fee for this trip (₹)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                placeholder="e.g. 60"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 focus:outline-none"
                data-testid="batch-delivery-fee-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter 0 for a free delivery. Charge is deducted from the member&apos;s prepaid balance
                — if it goes negative, they&apos;ll be blocked from new holds until they pay.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                Notes (optional)
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. handed over at gate"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 focus:outline-none"
                data-testid="batch-delivery-notes-input"
              />
            </div>

            {feeNum > 0 && (
              <div
                className={`rounded-lg px-4 py-2 text-sm ${
                  projectedBalance < 0
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-green-50 border border-green-200 text-green-700'
                }`}
                data-testid="projected-balance"
              >
                After this charge, balance will be{' '}
                <span className="font-semibold">₹{projectedBalance.toFixed(2)}</span>
                {projectedBalance < 0 && (
                  <span className="ml-1 inline-flex items-center gap-1">
                    <FiAlertTriangle /> member will be blocked from new holds
                  </span>
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-2 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
                disabled={submitting}
                data-testid="batch-delivery-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || selectedIds.length === 0}
                className="px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="batch-delivery-submit"
              >
                <FiCheck />
                {submitting
                  ? 'Marking…'
                  : `Mark ${selectedIds.length} hold${selectedIds.length === 1 ? '' : 's'} delivered`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
